import { useState } from 'react';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import {
  REFERENCE_TYPE_LABELS,
  REFERENCE_STATUS_LABELS,
  ReferenceStatus,
  ReferenceType,
  userInitials,
} from '../utils/airspace-helpers';
import { formatEventDate } from '../utils/date-utils';
import { buttonClass } from '../components/ui/Button';
import { Field, TextArea } from '../components/ui/Field';
import { CATEGORY } from '../components/ui/categoryColors';
import Select from '../components/ui/Select';
import Tooltip from '../components/ui/Tooltip';
import { FilePick } from '../components/ui/FilePick';
import { useConfirm } from '../components/ConfirmModal/ConfirmContext';

interface RefData {
  id: string;
  type: ReferenceType;
  reference_number: string | null;
  status: ReferenceStatus;
  notes: string | null;
  drive_file_id: string | null;
  drive_file_name: string | null;
  updated_at: string;
  updated_by_firstname: string | null;
  updated_by_lastname: string | null;
}

interface Props {
  reference: RefData;
  onChange: () => void;
}

const ALL_STATUSES: ReferenceStatus[] = ['not_submitted', 'pending', 'submitted', 'in_review', 'approved', 'denied'];

// The status colour is data: gray idle, amber pilot prep, orange sent, sky FAA reviewing, green approved, red denied.
const STATUS_HEX: Record<ReferenceStatus, string> = {
  not_submitted: CATEGORY.neutral.ink.hex,
  pending: CATEGORY.amber.ink.hex,
  submitted: CATEGORY.orange.ink.hex,
  in_review: CATEGORY.sky.ink.hex,
  approved: CATEGORY.green.ink.hex,
  denied: CATEGORY.red.ink.hex,
};

// Subtle row-background tints keyed to status, so the list reads at a glance.
// Same color family as the status pill, much lower opacity — the pill stays
// the loud signal; the row tint is just a quick orientation cue.
const ROW_BG_BY_STATUS: Record<ReferenceStatus, string> = {
  not_submitted: 'bg-surface-container border-outline-variant',
  pending:       'bg-amber-500/[0.07] border-amber-500/30',
  submitted:     'bg-orange-500/[0.07] border-orange-500/30',
  in_review:     'bg-sky-500/[0.07] border-sky-500/30',
  approved:      'bg-emerald-500/[0.07] border-emerald-500/30',
  denied:        'bg-red-500/[0.08] border-red-500/40',
};

const AirspaceReferenceRow: React.FC<Props> = ({ reference, onChange }) => {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const [refNumber, setRefNumber] = useState(reference.reference_number || '');
  const [notes, setNotes] = useState(reference.notes || '');
  const [editingNotes, setEditingNotes] = useState(false);
  const [uploading, setUploading] = useState(false);

  const updateField = async (patch: Partial<{ reference_number: string; status: ReferenceStatus; notes: string | null }>) => {
    try {
      await api.patch(`/airspace/references/${reference.id}`, { body: patch });
      onChange();
    } catch (err: any) {
      toast.error(err?.message || 'Update failed');
    }
  };

  const handleStatusChange = (s: ReferenceStatus) => updateField({ status: s });
  const handleRefBlur = () => {
    if (refNumber !== (reference.reference_number || '')) updateField({ reference_number: refNumber });
  };
  const handleNotesBlur = () => {
    setEditingNotes(false);
    if (notes !== (reference.notes || '')) updateField({ notes: notes || null });
  };

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/airspace/references/${reference.id}/file`, { body: fd });
      toast.success('File uploaded to Drive');
      onChange();
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFileRemove = async () => {
    if (!reference.drive_file_id) return;
    if (!(await confirm({ message: 'Remove the attached file from Drive?', confirmLabel: 'Remove', tone: 'danger' }))) return;
    try {
      await api.delete(`/airspace/references/${reference.id}/file`);
      toast.success('File removed');
      onChange();
    } catch (err: any) {
      toast.error(err?.message || 'Remove failed');
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({ message: `Remove this ${REFERENCE_TYPE_LABELS[reference.type]} reference?`, confirmLabel: 'Remove', tone: 'danger' }))) return;
    try {
      await api.delete(`/airspace/references/${reference.id}`);
      onChange();
    } catch (err: any) {
      toast.error(err?.message || 'Remove failed');
    }
  };

  const rowTint = ROW_BG_BY_STATUS[reference.status] || ROW_BG_BY_STATUS.not_submitted;

  return (
    <div className={`border rounded-md p-3 space-y-2.5 transition-colors ${rowTint}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-on-surface font-semibold mb-1">
            {REFERENCE_TYPE_LABELS[reference.type] || reference.type}
          </div>
          <Field
            type="text"
            aria-label={`FAA reference number for ${REFERENCE_TYPE_LABELS[reference.type] || reference.type}`}
            value={refNumber}
            onChange={(e) => setRefNumber(e.target.value)}
            onBlur={handleRefBlur}
            placeholder="FAA reference number…"
            className="font-mono"
          />
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <Select
            label="Status"
            size="sm"
            width="fit"
            value={reference.status}
            onChange={(k) => handleStatusChange(k as ReferenceStatus)}
            options={ALL_STATUSES.map(st => ({ key: st, label: REFERENCE_STATUS_LABELS[st], hex: STATUS_HEX[st] }))}
          />
          <span className="text-[10px] text-on-surface-variant">
            {userInitials(reference.updated_by_firstname, reference.updated_by_lastname)} ·{' '}
            {formatEventDate(reference.updated_at)}
          </span>
        </div>
      </div>

      {/* Notes */}
      {editingNotes ? (
        <TextArea
          aria-label="Notes for this reference"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={handleNotesBlur}
          autoFocus
          rows={2}
          placeholder="Notes for this reference…"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditingNotes(true)}
          className="text-xs text-on-surface-variant hover:text-on-surface italic block"
        >
          {notes ? <span className="not-italic text-on-surface-variant">{notes.length > 80 ? notes.slice(0, 80) + '…' : notes}</span> : '+ add notes'}
        </button>
      )}

      {/* File + actions */}
      <div className="flex items-center justify-between gap-2 text-xs pt-1 border-t border-gray-700/40">
        {reference.drive_file_id ? (
          <div className="flex items-center gap-2">
            <a
              href={`https://drive.google.com/file/d/${reference.drive_file_id}/view`}
              target="_blank"
              rel="noreferrer"
              className="text-primary hover:brightness-125"
            >
              {reference.drive_file_name} ↗
            </a>
            <button
              type="button"
              onClick={handleFileRemove}
              className={buttonClass('danger-quiet', 'sm')}
            >
              remove
            </button>
          </div>
        ) : (
          <FilePick
            variant="quiet"
            size="sm"
            accept=".pdf,.png,.jpg,.jpeg"
            disabled={uploading}
            onFiles={(files) => { handleFileUpload(files[0]); }}
          >
            {uploading ? 'uploading…' : '+ attach file (optional)'}
          </FilePick>
        )}
        <Tooltip label="Remove this reference row">
          <button
            type="button"
            onClick={handleDelete}
            className={buttonClass('danger-quiet', 'sm')}
          >
            remove row
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

export default AirspaceReferenceRow;
